/* The dashboard's views, in one place. The dashboard builds its navigation and
   headings from this, and the product page builds its list and its buttons
   from it, so the two cannot describe different products. src/routes.js lists
   the same ids so each view is prerendered and in the sitemap, and a test keeps
   the two lists in step.

   PRODUCT_NAME is the name the dashboard shows and writes into its page data.

   `toolbar` is which controls scope the screen: AC paths and meters, DC buses,
   or nothing. */

export const EXPLAINER_PATH = '/data-center-monitoring';
export const DEMO_PATH = '/data-center-monitoring/demo';

export const PRODUCT_NAME = 'SmartDC';

/* The first view lives at the demo's own address rather than at a second URL
   carrying the same page. */
export const demoLink = id => (id === 'site' ? DEMO_PATH : `${DEMO_PATH}/${id}`);

export const SCREEN_GROUPS = ['Site', 'AC distribution', 'UPS DC bus', 'Workspace'];

export const SCREENS = [
    {
        id: 'site',
        group: 'Site',
        title: 'Site overview',
        subtitle: 'Every stage of the power chain, on both paths, at a glance.',
        toolbar: 'none',
        text: 'The whole site in one view: the power chain from the utility to the rack on both paths, what is protected and how much margin is left, how long the batteries would last, and every active alarm.'
    },
    {
        id: 'ac',
        group: 'AC distribution',
        title: 'AC power quality',
        subtitle: 'Measure the supply. See what reaches the load.',
        toolbar: 'ac',
        text: 'The live state of the supply at whichever meter you choose: voltage, current, frequency, real and apparent power and power factor on each phase, with the conversion path, a phase diagram, line voltages, unbalance, neutral current and 24 hours of energy and demand.'
    },
    {
        id: 'harmonics',
        group: 'AC distribution',
        title: 'Harmonics & waveform',
        subtitle: 'Understand distortion, phase balance and the shape of your power.',
        toolbar: 'ac',
        text: 'Distortion broken down order by order, from the 2nd harmonic to the 50th, for voltage or current and for each phase. Alongside it, the shape of the waveform, neutral loading, crest factor and flicker, with waveform distortion kept apart from the demand-based figure a compliance study uses.'
    },
    {
        id: 'events',
        group: 'AC distribution',
        title: 'Power quality events',
        subtitle: 'Connect upstream disturbances with the protected UPS output.',
        toolbar: 'events',
        text: 'Every sag, swell, interruption and transient, with how deep it went, how long it lasted and which phases it touched. The disturbance upstream sits next to what the UPS output did at the same moment, so you can see whether it ever reached the load.'
    },
    {
        id: 'tests',
        group: 'AC distribution',
        title: 'PQ test workspace',
        subtitle: 'Evaluate measured values against your operating criteria. Keep the evidence.',
        toolbar: 'ac',
        text: 'Take a reading from a meter or import one from a CSV file, and check it against limits you set: voltage, frequency, distortion, unbalance, power factor and phase rotation. Each run freezes its evidence and its limits, and exports as a CSV report or a JSON record.'
    },
    {
        id: 'overview',
        group: 'UPS DC bus',
        title: 'UPS DC overview',
        subtitle: 'The DC link between your rectifier, battery and inverter sections.',
        toolbar: 'dc',
        text: 'Both 800 V buses at a glance: power, voltage, loading, a one-line diagram, demand trends and every feeder. Choose a feeder and you get its voltage, current, power and joint temperature, its breaker state, and what its load would become if its paired feed were lost.'
    },
    {
        id: 'electrical',
        group: 'UPS DC bus',
        title: 'DC electrical',
        subtitle: 'Bus voltage, current, DC ripple and captured disturbances.',
        toolbar: 'dc',
        text: 'Pole-to-pole and pole-to-earth voltage, current, ripple and power for each bus, the loss across the distribution, and triggered captures of the fast dips a slow display would never catch.'
    },
    {
        id: 'thermal',
        group: 'UPS DC bus',
        title: 'Thermal health',
        subtitle: 'Find developing hot spots across bus joints and tap-off terminations.',
        toolbar: 'dc',
        text: 'A map of every bus joint and tap-off, by temperature or by rise above ambient, with rate of rise, voltage drop and estimated resistance. A joint running hotter than its neighbours stands out, and a sensor that has stopped reporting shows as unknown, not as fine.'
    },
    {
        id: 'protection',
        group: 'UPS DC bus',
        title: 'Protection & alarms',
        subtitle: 'Insulation, residual current, device status and actionable events.',
        toolbar: 'dc',
        text: 'Active conditions explained in plain language, insulation and residual current, protective device status, alarm thresholds and event history. Acknowledging an alarm records that someone saw it, and the condition stays active until it actually clears.'
    },
    {
        id: 'energy',
        group: 'UPS DC bus',
        title: 'Energy & backup',
        subtitle: 'Understand consumption, source capacity and backup autonomy.',
        toolbar: 'dc',
        text: 'Energy and rolling demand, the capacity left if one path is lost, the status of every rectifier module, and each battery’s charge, health and estimated runtime.'
    },
    {
        id: 'health',
        group: 'Workspace',
        title: 'System health',
        subtitle: 'Know which measurements you can trust and where coverage is missing.',
        toolbar: 'none',
        text: 'Which sensors are reporting, how the gateways and clocks are doing, which channels have gone stale, and what is still waiting on commissioning. It is how you know how far to trust every other reading.'
    },
    {
        id: 'library',
        group: 'Workspace',
        title: 'Measurement library',
        subtitle: 'AC power quality and UPS DC monitoring, with how each value is measured.',
        toolbar: 'none',
        text: 'Every measurement defined in one searchable, exportable place: its unit, its type, the instrument behind it and how to read it, with the standards and technical references behind them.'
    },
    {
        id: 'reports',
        group: 'Workspace',
        title: 'Reports',
        subtitle: 'A daily operations report, ready to print or export.',
        toolbar: 'none',
        text: 'A daily operations report written from the same readings: energy, peak demand, capacity, backup, the hottest joints, power quality events and the alarm log, ready to print, save as a PDF or export.'
    },
    {
        id: 'simulator',
        group: 'Workspace',
        title: 'Fault simulator',
        subtitle: 'Inject a fault and watch the whole system respond.',
        toolbar: 'none',
        text: 'See what the system does on a bad day. Take out a utility feed or a whole UPS path, overheat a joint, fail a rectifier module or fault the insulation, and every reading, diagram and alarm responds.'
    }
];
